/** Long, sparse rail that gives the cull bay its receding rhythm. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone, type ZonePalette } from '@/render/palette';

const zone: ZonePalette = (() => {
  const found = getZone('cull');
  if (!found) throw new Error('overhead rail: cull zone missing from the palette');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`overhead rail: cull surface ${String(index)} missing`);
  return colour;
}

function steelBox(size: readonly [number, number, number], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), material);
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'overhead-rail';
  const steel = surface('metal', zoneSurface(2));
  const darkSteel = surface('metal', zoneSurface(1));

  // The flange, not the box beam, defines this asset's top face. The 2.15 m
  // room datum belongs to the caller now: leaving it in this factory made the
  // rail look right in the cull bay while failing every other placement and the
  // ground-origin contract in `docs/asset-conventions.md`.
  const beam = steelBox([0.05, 0.05, 4.4], steel, 'square-box-beam');
  beam.position.y = 0.14;
  const flange = steelBox([0.09, 0.02, 4.4], darkSteel, 'top-flange');
  flange.position.y = 0.205;
  group.add(beam, flange);

  for (let index = 0; index < 8; index += 1) {
    // The end hooks sit 0.275 m inside the beam ends. Placing a hook centre at
    // +/-2.2 made its tiny return bar silently grow the declared 4.4 m run.
    const z = -1.925 + index * 0.55;
    const hook = new THREE.Group();
    hook.name = `rail-hook-${index + 1}`;
    hook.position.z = z;
    for (const [x, name] of [[-0.035, 'left'], [0.035, 'right']] as const) {
      const side = steelBox([0.015, 0.10, 0.015], darkSteel, `hook-${name}-side`);
      side.position.set(x, 0.065, 0);
      hook.add(side);
    }
    const returnBar = steelBox([0.085, 0.015, 0.015], darkSteel, 'hook-return-bar');
    returnBar.position.y = 0.0075;
    hook.add(returnBar);
    group.add(hook);
  }

  return group;
}

export const overheadRailAsset: AssetDefinition = {
  id: 'overhead-rail',
  label: 'Cull-bay overhead rail',
  description: '4.4 m steel rail with regular U hooks for the obscured bay.',
  footprint: { width: 0.09, height: 0.215, length: 4.4 },
  create: build,
};
